import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';

export interface CreateTaskRequest {
  entityCd: string;
  taskTypeCd: string;
  taskTypeName: string;
  taskTitle: string;
  taskFlow: {
    createDocument: boolean;
    requestTSign: boolean;
    proceedWithEFiling: boolean;
  };
  templateIds?: string[];
  officerData?: Record<string, any>;
}

export interface CreateTaskResponse {
  taskId: number;
  taskTitle: string;
  templateMaps: Array<{
    tmpId: string;
    mapId: number;
  }>;
  docCustSaves?: Array<{
    tmpId: string;
    mapId: number;
    saveId: number;
    fileName: string;
  }>;
  message: string;
}

export interface TaskDetail {
  TASK_ID: number;
  CLIENT_CD: string;
  ENTITY_CD: string;
  TASK_TYPE_CD: string;
  TASK_TYPE_NAME: string;
  COUNTRY_CD: string;
  TASK_STATUS: string;
  TASK_TITLE: string;
  TASK_COMPLETED_DATE: string | null;
  IS_CREATE_DOCUMENT: boolean;
  IS_T_SIGN: boolean;
  IS_E_FILING: boolean;
  T_SIGN_STATUS: string;
  E_FILING_STATUS: string;
  CREATE_DOCUMENT_STATUS: string;
  TEMPLATE_COUNT: number;
}

/**
 * 타스크 신규생성
 */
export const createTask = async (data: CreateTaskRequest): Promise<ApiResponse<CreateTaskResponse>> => {
  const response = await apiClient.post<CreateTaskResponse>('/startTask/tasks', data);
  return response;
};

/**
 * 타스크 상세 조회
 */
export const getTask = async (taskId: number): Promise<ApiResponse<{ task: TaskDetail }>> => {
  const response = await apiClient.get<{ task: TaskDetail }>(`/startTask/tasks/${taskId}`);
  return response;
};

/**
 * Register 성공 후 L_OFFICER 동기화 (Task 기반)
 */
export const registerTaskOfficer = async (
  taskId: number | string,
): Promise<ApiResponse<{ officerIdx?: number; alreadyExists?: boolean; message?: string }>> => {
  const response = await apiClient.post<{ officerIdx?: number; alreadyExists?: boolean; message?: string }>(
    `/startTask/tasks/${taskId}/register-officer`,
    {},
  );
  return response;
};

/**
 * Register 성공 후 L_CONTROLLERMANAGE 동기화 (PreSet B32007/B32106/B32107)
 * IS_SYNC=0, REFERENCES_CONTACT_CD=contactCd, SYSMOD* 미설정
 */
export const registerTaskControllerManage = async (
  taskId: number | string,
): Promise<ApiResponse<{ ctrlIdx?: number; alreadyExists?: boolean; message?: string }>> => {
  const response = await apiClient.post<{ ctrlIdx?: number; alreadyExists?: boolean; message?: string }>(
    `/startTask/tasks/${taskId}/register-controller-manage`,
    {},
  );
  return response;
};

/**
 * Register 전 Task 기반 contact 보장
 */
export const ensureTaskRegisterContact = async (
  taskId: number | string,
): Promise<ApiResponse<{ contactCd?: string; created?: boolean; taskData?: Record<string, any>; message?: string }>> => {
  const response = await apiClient.post<{ contactCd?: string; created?: boolean; taskData?: Record<string, any>; message?: string }>(
    `/startTask/tasks/${taskId}/ensure-register-contact`,
    {},
  );
  return response;
};

/**
 * Task ID로 L_DOC_CUST_SAVE 목록 조회 (치환된 문서)
 */
export interface DocCustSaveDocument {
  saveId: number;
  taskId: number;
  mapId: number;
  fileName: string;
  tmpId: number;
  docRendered?: string; // Base64 인코딩된 DOCX 파일
  docStatus: string;
  dataJson?: string;
  signerJson?: any[]; // SIGNER_JSON (서명자 정보)
  taskData?: any; // TASK_DATA (L_START_TASK에서 가져온 폼 데이터)
  taskTypeCd?: string;
  taskTypeName?: string;
  taskTitle?: string;
  entityName?: string;
  registrationNo?: string;
  personName?: string;
  personRole?: string;
}

export const getDocCustSaveDocuments = async (
  taskId: number | string,
  options?: { alignTsignDraft?: boolean }
): Promise<ApiResponse<{ documents: DocCustSaveDocument[] }>> => {
  const params = options?.alignTsignDraft ? { alignTsignDraft: '1' } : undefined;
  const response = await apiClient.get<{ documents: DocCustSaveDocument[] }>(
    `/startTask/doc-cust-save/${taskId}`,
    params ? { params } : undefined
  );
  return response;
};

/** SAVE_ID 목록으로 L_DOC_CUST_SAVE 문서 조회 (standalone T-Sign용) */
export const getDocsBySaveIds = async (ids: string): Promise<{ success: boolean; documents: Array<{ saveId: number; fileName: string; fileSize: number; signerJson?: any[] }> }> => {
  const response = await apiClient.get<{ documents: Array<{ saveId: number; fileName: string; fileSize: number; signerJson?: any[] }> }>(`/tsign/documents/by-save-ids`, { params: { ids } });
  return response as any;
};

/**
 * L_DOC_CUST_SAVE 문서 강제 저장
 * OnlyOffice Command Service를 통해 문서를 강제 저장
 */
export interface ForceSaveResponse {
  success: boolean;
  message: string;
  saveId?: string;
  key?: string;
  warning?: boolean;
  errorCode?: number;
}

export const forceSaveDocCustSave = async (saveId: number | string, key: string): Promise<ApiResponse<ForceSaveResponse>> => {
  const response = await apiClient.post<ForceSaveResponse>(`/startTask/doc-cust-save-file/${saveId}/force-save`, { key });
  return response;
};

/**
 * 직접 저장 응답 인터페이스
 */
export interface DirectSaveResponse {
  success: boolean;
  message: string;
  saveId: number | string;
  rowsAffected?: number;
  noChanges?: boolean; // 직접 저장에서도 변경사항이 없는 경우
  errorCode?: string; // 에러 코드 (예: 'NO_DOCUMENT_URL')
  requiresRefresh?: boolean; // drop 명령으로 저장된 경우 새로고침 필요
  suggestion?: string; // 에러 발생 시 사용자에게 보여줄 제안
  note?: string; // 추가 안내 메시지
}

/**
 * OnlyOffice Document Server에서 문서를 직접 다운로드하여 DB에 저장
 * error: 4가 반환되었지만 실제로 변경사항이 있는 경우 사용
 * @param saveId - 문서 저장 ID
 * @param key - OnlyOffice Document Server의 문서 key
 */
export const directSaveDocCustSave = async (saveId: number | string, key: string): Promise<ApiResponse<DirectSaveResponse>> => {
  const response = await apiClient.post<DirectSaveResponse>(`/startTask/doc-cust-save-file/${saveId}/direct-save`, { key });
  return response;
};

/**
 * 서명자 정보 인터페이스
 */
export interface Signer {
  name: string;
  email: string;
  role: 'signer' | 'viewer';
}

/**
 * SIGNER_JSON 업데이트 응답
 */
export interface UpdateSignersResponse {
  message: string;
  saveId?: number;
  taskId?: number;
  signersCount: number;
}

/**
 * 특정 문서(SAVE_ID)의 SIGNER_JSON 업데이트
 */
export const updateDocumentSigners = async (saveId: number | string, signers: Signer[]): Promise<ApiResponse<UpdateSignersResponse>> => {
  const response = await apiClient.put<UpdateSignersResponse>(`/startTask/doc-cust-save/${saveId}/signers`, { signers });
  return response;
};

/**
 * Task의 모든 문서에 SIGNER_JSON 업데이트
 */
export const updateTaskSigners = async (taskId: number | string, signers: Signer[]): Promise<ApiResponse<UpdateSignersResponse>> => {
  const response = await apiClient.put<UpdateSignersResponse>(`/startTask/doc-cust-save/task/${taskId}/signers`, { signers });
  return response;
};

/**
 * T-Sign 문서 생성 요청 인터페이스
 */
export interface CreateTSignDocumentRequest {
  /** Task 없이 메뉴에서만 T-Sign 할 때 생략 */
  taskId?: number | string | null;
  /** Compose 재진입 시 기존 Draft DOC_GUID 재사용 */
  docGuid?: string;
  documentTitle: string;
  emailTitle: string;
  emailBody: string;
  /** standalone일 때 서버 jurisdiction 외 추가 지정용 */
  entityCd?: string;
  countryCd?: string;
  recipients: Array<{
    name: string;
    email: string;
    role: 'signer' | 'viewer';
  }>;
  files: Array<{
    pdfGuid?: string;
    saveId?: number;
    fileName: string;
    totalPage: number;
    fileSize: number;
    physicalPath?: string;
  }>;
  /** Compose에서 제거한 SAVE_ID — Variable Data 동기화용 soft-delete */
  removedSaveIds?: number[];
}

/**
 * T-Sign 문서 생성 응답 인터페이스
 */
export interface CreateTSignDocumentResponse {
  docGuid: string;
  taskId: number | null;
  documentTitle: string;
  participants: Array<{
    pcpGuid: string;
    rcpGuid: string;
    name: string;
    email: string;
    role: string;
    index: number;
  }>;
  pdfs: Array<{
    pdfGuid: string;
    saveId: number;
    fileName: string;
    logicalPath: string;
    physicalPath: string;
    index: number;
  }>;
  status: string;
}

/**
 * T-Sign 문서 생성
 * L_UT_TSIGN_DOCUMENT, L_UT_TSIGN_PARTICIPANT, L_UT_TSIGN_PDF에 데이터 저장
 * L_DOC_CUST_SAVE.DOC_GUID 업데이트
 */
export const createTSignDocument = async (data: CreateTSignDocumentRequest): Promise<ApiResponse<CreateTSignDocumentResponse>> => {
  const response = await apiClient.post<CreateTSignDocumentResponse>('/tsign/documents', data);
  return response;
};

/**
 * T-Sign 서명 요청 전송 응답 인터페이스
 */
export interface SendTSignDocumentResponse {
  docGuid: string;
  taskId: number | null;
  docStatus: string;
  emailResults: Array<{
    pcpGuid: string;
    email: string;
    name: string;
    success: boolean;
    error?: string;
  }>;
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}

/**
 * T-Sign 서명 요청 전송
 * - 서명자(Signer)에게 이메일 전송
 * - L_UT_TSIGN_DOCUMENT.DOC_STATUS: 'D' → 'R'
 * - L_UT_TSIGN_AUDIT: 이메일 전송 기록
 * - L_START_TASK.CREATE_DOCUMENT_STATUS: 완료로 변경
 */
export const sendTSignDocument = async (
  docGuid: string,
  taskId?: number | string,
  /** 켜면 각 서명자에게 본인 서명칸이 있는 문서만 보여준다(문서 가시성). 발송 시점에 확정된다 */
  signerDocOnly?: boolean
): Promise<ApiResponse<SendTSignDocumentResponse>> => {
  const response = await apiClient.post<SendTSignDocumentResponse>(`/tsign/documents/${docGuid}/send`, {
    taskId,
    signerDocOnly: !!signerDocOnly
  });
  return response;
};

/**
 * T-Sign 문서 상세 조회 응답 인터페이스
 */
export interface TSignDocumentDetail {
  document: {
    docGuid: string;
    clientCd: string;
    entityCd: string;
    entityName: string;
    taskId?: number | null;
    docTitle: string;
    docStatus: string;
    emailTitle: string;
    emailBody: string;
    mergedPdfUrl: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    tSignStatus?: string; // T_SIGN_STATUS (B35003: 완료)
  };
  participants: Array<{
    pcpGuid: string;
    rcpGuid: string;
    rcpIndex: number;
    pcpType: string;
    pcpStatus: string;
    name: string;
    email: string;
    role: 'signer' | 'viewer';
    sent?: string | null;
    viewed?: string | null;
    signed?: string | null;
  }>;
  pdfs: Array<{
    pdfGuid: string;
    fileName: string;
    logicalPath: string;
    fileSize: number;
    totalPage: number;
    pdfIndex: number;
    saveId?: number | null;
  }>;
}

/** Task에 연결된 T-Sign 문서 요약 (Draft 재진입용) */
export interface TSignTaskSummaryItem {
  docGuid: string;
  docTitle: string;
  docStatus: string;
  requestedAt: string | null;
  updatedAt: string | null;
  participantCount: number;
  pdfCount: number;
  saveIds: number[];
}

export const getTSignTaskSummary = async (
  taskId: number | string,
): Promise<ApiResponse<{ data: TSignTaskSummaryItem | null; items: TSignTaskSummaryItem[] }>> => {
  const response = await apiClient.get<{ data: TSignTaskSummaryItem | null; items: TSignTaskSummaryItem[] }>(
    `/tsign/documents/task/${taskId}/summary`,
  );
  return response;
};

/**
 * T-Sign 문서 상세 조회
 * L_UT_TSIGN_DOCUMENT, L_UT_TSIGN_PARTICIPANT, L_UT_TSIGN_PDF 조회
 */
export const getTSignDocument = async (docGuid: string): Promise<ApiResponse<TSignDocumentDetail>> => {
  const response = await apiClient.get<TSignDocumentDetail>(`/tsign/documents/${docGuid}`);
  return response;
};

/**
 * 서명 도구(Sign Tool) 저장 요청 인터페이스
 */
export interface SignTool {
  id: string;
  toolType: number;
  /** 전 파일을 이어붙인 전역 페이지 번호 */
  page: number;
  /** 서명칸이 놓인 파일. 문서 가시성 판정이 이 값만 본다(페이지 역산 없음) */
  pdfGuid?: string;
  /** 그 파일 안에서 몇 페이지인지 (1-base) */
  pageInFile?: number | null;
  x: number;
  y: number;
  width: number;
  height: number;
  signerPcpGuid: string;
  signerIndex?: number;
  signerEmail?: string;
  signerName?: string;
  required?: boolean;
  dataLabel?: string;
  tooltip?: string;
  style?: string;
  css?: string;
  docWidth?: number;
  docHeight?: number;
  // 텍스트·날짜 필드용 속성
  fontName?: string;
  fontSize?: number;
  dateFormat?: string;
  inputFormat?: string;
  align?: string;
  /** 작성자가 미리 채운 값 — 서명자가 고치지 않으면 그대로 출력된다 */
  defaultValue?: string;
  signerEditable?: boolean;
}

/**
 * 서명 도구 저장 응답 인터페이스
 */
export interface SaveSignToolsResponse {
  docGuid: string;
  tools: Array<{
    tlGuid: string;
    tlId: string;
    page: number;
    toolType: number;
    signerPcpGuid: string;
  }>;
  count: number;
}

/**
 * T-Sign 서명 도구 저장
 * L_UT_TSIGN_TOOL 테이블에 서명 도구 데이터 저장
 * DOC_GUID 기준으로 기존 데이터 삭제 후 새로 삽입
 */
export const saveSignTools = async (docGuid: string, tools: SignTool[]): Promise<ApiResponse<SaveSignToolsResponse>> => {
  const response = await apiClient.post<SaveSignToolsResponse>(`/tsign/documents/${docGuid}/tools`, { tools });
  return response;
};

/**
 * Task 문서 상태 업데이트 응답 인터페이스
 */
export interface UpdateDocumentStatusResponse {
  message: string;
  taskId: number;
  oldStatus?: string;
  newStatus: string;
  totalCount: number;
  savedCount: number;
}

/**
 * Task의 CREATE_DOCUMENT_STATUS 업데이트
 * 문서 저장 상태에 따라 B34002 (In Progress) 또는 B34003 (Complete)로 업데이트
 */
export const updateTaskDocumentStatus = async (taskId: number | string): Promise<ApiResponse<UpdateDocumentStatusResponse>> => {
  const response = await apiClient.put<UpdateDocumentStatusResponse>(`/startTask/tasks/${taskId}/document-status`);
  return response;
};

/**
 * Ignore Callback 응답 인터페이스
 */
export interface IgnoreCallbackResponse {
  success: boolean;
  message: string;
  saveId: string;
  expiresIn?: string;
}

/**
 * ignore-callback 플래그 설정
 * "Discard Changes" 선택 시 다음 콜백을 무시하도록 플래그 설정
 *
 * 문제 상황:
 * autosave: true 설정 시, 사용자가 "Discard Changes"를 선택해도
 * OnlyOffice의 autosave가 에디터 닫힘(탭 변경) 시 콜백을 호출하여 변경사항을 저장함
 *
 * 해결 방법:
 * 1. 탭 변경 전에 이 API를 호출하여 ignore 플래그 설정
 * 2. 백엔드 콜백 핸들러에서 ignore 플래그가 있으면 저장을 건너뜀
 * 3. 플래그는 한 번 사용되면 자동으로 삭제됨 (1회성)
 *
 * @param saveId - 문서 저장 ID
 * @param reason - 무시 이유 (선택적)
 */
export const setIgnoreCallback = async (saveId: number | string, reason?: string): Promise<ApiResponse<IgnoreCallbackResponse>> => {
  const response = await apiClient.post<IgnoreCallbackResponse>(
    `/startTask/doc-cust-save-file/${saveId}/ignore-callback`,
    { reason: reason || 'Discard Changes' }
  );
  return response;
};

/**
 * ignore-callback 플래그 해제 (선택적)
 * 프론트엔드에서 명시적으로 해제할 때 사용
 *
 * @param saveId - 문서 저장 ID
 */
export const clearIgnoreCallback = async (saveId: number | string): Promise<ApiResponse<IgnoreCallbackResponse>> => {
  const response = await apiClient.delete<IgnoreCallbackResponse>(
    `/startTask/doc-cust-save-file/${saveId}/ignore-callback`
  );
  return response;
};

/**
 * E-Filing 완료 상태 업데이트 응답 인터페이스
 */
export interface UpdateEFilingStatusResponse {
  success: boolean;
  message: string;
  taskId: number | string;
  eFilingStatus: string;
  taskStatus: string;
}

/**
 * E-Filing 완료 시 상태 업데이트
 * L_START_TASK 테이블의 E_FILING_STATUS를 B36003(완료)로,
 * TASK_STATUS를 B33003(완료)로 업데이트
 *
 * @param taskId - Task ID
 */
export const updateEFilingComplete = async (taskId: number | string): Promise<ApiResponse<UpdateEFilingStatusResponse>> => {
  const response = await apiClient.put<UpdateEFilingStatusResponse>(
    `/startTask/tasks/${taskId}/e-filing-complete`
  );
  return response;
};

/**
 * L_START_TASK.E_FILING_STATUS 갱신 (예: T-Sign 완료 후 B36002 Ready)
 */
export const patchTaskEFilingStatus = async (
  taskId: number | string,
  eFilingStatus: string,
): Promise<ApiResponse<UpdateEFilingStatusResponse>> => {
  const response = await apiClient.patch<UpdateEFilingStatusResponse>(
    `/startTask/tasks/${taskId}/e-filing-status`,
    { eFilingStatus },
  );
  return response;
};

/**
 * Task 데이터 업데이트 (TASK_DATA 컬럼)
 * E-Filing 완료 후 폼 데이터 및 결과 저장용
 *
 * @param taskId - Task ID
 * @param taskData - 저장할 데이터 객체 (기존 TASK_DATA와 병합됨)
 */
export const updateTaskFormData = async (
  taskId: number | string,
  taskData: Record<string, unknown>
): Promise<ApiResponse<{ success: boolean; message: string }>> => {
  const response = await apiClient.put<{ success: boolean; message: string }>(
    `/startTask/tasks/${taskId}/data`,
    { taskData }
  );
  return response;
};

/**
 * e-Filing 데이터 저장 페이로드 — L_TASK_EFILING_DATA INSERT
 */
export interface SaveEFilingDataPayload {
  taskTypeCd?: string | null;
  entityCd?: string | null;
  countryCd?: string | null;
  formData?: Record<string, unknown> | null;
  requestPayload?: Record<string, unknown> | null;
  responseData?: Record<string, unknown> | null;
  warningMessage?: string | null;
  receiptPdfBase64?: string | null;
  receiptPdfFilename?: string | null;
  status?: string;
  isSuccess?: boolean;
  errorMessage?: string | null;
  submittedAt?: string | null;
  completedAt?: string | null;
  isDraft?: boolean;
}

/**
 * e-Filing 데이터 저장 (L_TASK_EFILING_DATA).
 *   - taskId 가 있으면 일반 task 연동 흐름 → POST /:taskId/efiling-data (IS_STANDALONE=0).
 *   - taskId 가 null/undefined 면 standalone 흐름 → POST /efiling-data/standalone (TASK_ID=NULL, IS_STANDALONE=1).
 *   isDraft=true 시 STATUS=B36001 / IS_SUCCESS=0 강제 + 같은 매칭 키의 latest Draft row UPSERT.
 */
export const saveEFilingData = async (
  taskId: number | string | null | undefined,
  payload: SaveEFilingDataPayload,
): Promise<ApiResponse<{ efilingId: number; taskId: number | null; message: string }>> => {
  if (taskId == null || taskId === '') {
    return apiClient.post('/startTask/tasks/efiling-data/standalone', payload);
  }
  return apiClient.post(`/startTask/tasks/${taskId}/efiling-data`, payload);
};

/**
 * e-Filing 데이터 조회.
 *   기본: 가장 최근 1건. all=true 시 전체 이력. include='pdf' 시 PDF 포함.
 *   efilingId 지정 시 해당 L_TASK_EFILING_DATA 행만 (목록에서 특정 이력 조회용).
 *   efilingId=0: L_TASK_EFILING_DATA 에 행이 없을 때만 L_START_TASK.TASK_DATA JSON 의 eFilingResult 사용.
 */
export const getEFilingData = async (
  taskId: number | string,
  options: { all?: boolean; includePdf?: boolean; efilingId?: number | string } = {},
): Promise<ApiResponse<any>> => {
  const params = new URLSearchParams();
  if (options.all) params.set('all', 'true');
  if (options.includePdf) params.set('include', 'pdf');
  if (options.efilingId !== undefined && options.efilingId !== null && String(options.efilingId).trim() !== '') {
    params.set('efilingId', String(options.efilingId));
  }
  const qs = params.toString();
  return apiClient.get(`/startTask/tasks/${taskId}/efiling-data${qs ? `?${qs}` : ''}`);
};

/**
 * 레지스터 등록 응답 인터페이스
 */
export interface RegisterToRegisterResponse {
  success: boolean;
  message: string;
  taskId: number;
  rgstCd?: string;
  apiType?: string;
  registerWork?: string;
}

/**
 * 레지스터 등록 (Deprecated - registerFromContact API 사용 권장)
 * Task의 Officer/Controller 정보를 L_REGISTER 테이블에 저장
 *
 * @param taskId - Task ID
 * @deprecated Use registerDirector, registerSecretary, registerCeo, registerController instead
 */
export const registerToRegister = async (taskId: number | string): Promise<ApiResponse<RegisterToRegisterResponse>> => {
  const response = await apiClient.post<RegisterToRegisterResponse>(
    `/startTask/tasks/${taskId}/register-to-register`
  );
  return response;
};

// ===== registerFromContact API =====

/**
 * Director/Secretary/CEO 등록 요청 인터페이스
 */
export interface RegisterOfficerRequest {
  entityCd: string;
  contactCd: string;
  appointmentDate: string;
  cessationDate?: string;
  registerType?: string;
  bigo?: string;
  /** PreSet(M-코드) 판별용 — backend 가 M-코드면 L_REGISTER 생략. */
  taskTypeCd?: string;
}

/**
 * Controller 등록 요청 인터페이스
 */
export interface RegisterControllerRequest {
  entityCd: string;
  contactCd: string;
  dateOfEntry?: string;
  dateOfAppointed?: string;
  dateOfCessation?: string;
  cessationReason?: string;
  aliases?: string;
  legalForm?: string;
  jurisdiction?: string;
  incorporationStatute?: string;
  corporateEntityRegister?: string;
  nonAcrAuen?: string;
  dateOfConfirmation?: string;
  categoryCode?: string;
  registerType?: string;
  bigo?: string;
  taskTypeCd?: string; // B32007 (Controller Appointment) 또는 B32008 (Controller Cessation)
  registrableControllerId?: string; // B32008인 경우 L_CONTROLLERMANAGE 조회용
}

/**
 * 레지스터 등록 응답 인터페이스 (registerFromContact)
 */
export interface RegisterFromContactResponse {
  rgstCd: string;
  registerType: string;
  registerWork: string;
  message: string;
}

/**
 * Director 레지스터 등록
 * POST /api/registerFromContact/director
 */
export const registerDirector = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/director', data);
  return response;
};

/**
 * Director 레지스터 등록 — Malaysia 세션 전용
 * POST /api/registerFromContact/my/director
 * (백엔드 routes/registerFromContact/my/director.js — 프로시저 호출 없이 직접 JSON 생성)
 */
export const registerMyDirector = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/my/director', data);
  return response;
};

/**
 * Secretary 레지스터 등록
 * POST /api/registerFromContact/secretary
 */
export const registerSecretary = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/secretary', data);
  return response;
};

/**
 * Secretary 레지스터 등록 — Malaysia 세션 전용
 * POST /api/registerFromContact/my/secretary
 */
export const registerMySecretary = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/my/secretary', data);
  return response;
};

/**
 * CEO 레지스터 등록
 * POST /api/registerFromContact/ceo
 */
export const registerCeo = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/ceo', data);
  return response;
};

/**
 * Data Protection Officer 레지스터 등록 (Register of DPOs, REGISTER_WORK A50018)
 * POST /api/registerFromContact/dpo
 */
export const registerDpo = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/dpo', data);
  return response;
};

/**
 * Controller 레지스터 등록
 * POST /api/registerFromContact/controller
 */
export const registerController = async (data: RegisterControllerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/controller', data);
  return response;
};

/**
 * Beneficial Owner 레지스터 등록 — Malaysia 세션 전용
 * POST /api/registerFromContact/my/beneficial-owner
 */
export const registerMyBeneficialOwner = async (data: Record<string, unknown>): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/my/beneficial-owner', data);
  return response;
};

/**
 * Auditor 레지스터 등록
 * POST /api/registerFromContact/auditor
 */
export const registerAuditor = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/auditor', data);
  return response;
};

/**
 * Managing Director 레지스터 등록
 * POST /api/registerFromContact/managing-director
 */
export const registerManagingDirector = async (data: RegisterOfficerRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/managing-director', data);
  return response;
};

/**
 * Alternate Director 레지스터 등록 요청 인터페이스
 */
export interface RegisterAlternateDirectorRequest extends RegisterOfficerRequest {
  alternateDirector: {
    directorCd: string;
  };
}

/**
 * Alternate Director 레지스터 등록
 * POST /api/registerFromContact/alternate-director
 */
export const registerAlternateDirector = async (data: RegisterAlternateDirectorRequest): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/alternate-director', data);
  return response;
};

/**
 * Allotment Shares 레지스터 등록 요청 인터페이스
 */
export interface RegisterAllotmentSharesRequest {
  entityCd: string;
  jsonDataList: Array<{
    contactCd: string;
    currency?: string;
    CURRENCY?: string;
    CURRENCY_CD?: string;
    CONTACT_NAME: string;
    'class of shares'?: string;
    CLASS_OF_SHARES?: string;
    CLASS_OF_SHARES_CD?: string;
    NUMBER_OF_SHARES?: string;
    ISSUED_SHARE_CAPITAL?: string;
    PAID_UP_SHARE_CAPITAL?: string;
    SUB_CLASS?: string;
    TRANSACTION_DATE?: string;
    CERTI_NO?: string;
    CERTIFICATE_NO?: string;
    CERTI_TYPE?: string;
    CERTIFICATE_TYPE?: string;
    [key: string]: any;
  }>;
}

/**
 * Allotment Shares 레지스터 등록 응답 인터페이스
 */
export interface RegisterAllotmentSharesResponse {
  rgstCds: {
    allotments: string[];
    members: string[];
    directorShareholdings: string[];
    ceoShareholdings: string[];
  };
  count: number;
  message: string;
}

/**
 * Allotment Shares 레지스터 등록
 * POST /api/register/allotmentShares
 */
export const registerAllotmentShares = async (data: RegisterAllotmentSharesRequest): Promise<ApiResponse<RegisterAllotmentSharesResponse>> => {
  const response = await apiClient.post<RegisterAllotmentSharesResponse>('/register/allotmentShares', data);
  return response;
};

/**
 * Transfer Shares 레지스터 등록 요청 인터페이스
 */
export interface RegisterTransferSharesRequest {
  entityCd: string;
  jsonDataList: Array<{
    contactCd: string;        // From ContactCd
    toContactCd: string;      // To ContactCd
    currency: string;
    'class of shares': string;
    NUMBER_OF_SHARES: string;
    TRANSACTION_DATE: string;
    CERTI_NO: string;         // From Certificate No
    CERTI_TYPE: string;       // From Certificate Type
    TO_CERTI_NO: string;      // To Certificate No
    TO_CERTI_TYPE: string;    // To Certificate Type
    [key: string]: any;
  }>;
}

/**
 * Transfer Shares 레지스터 등록 응답 인터페이스
 */
export interface RegisterTransferSharesResponse {
  rgstCds: {
    transfers: string[];
  };
  count: number;
  message: string;
}

/**
 * Transfer Shares 레지스터 등록
 * POST /api/register/transferShares
 */
export const registerTransferShares = async (data: RegisterTransferSharesRequest): Promise<ApiResponse<RegisterTransferSharesResponse>> => {
  const response = await apiClient.post<RegisterTransferSharesResponse>('/register/transferShares', data);
  return response;
};

/**
 * Address 레지스터 등록 요청 인터페이스
 *   Change Address(M32102) Task 의 Register 단계에서 사용.
 *
 *   addressData 키는 백엔드(`/registerFromContact/my/address.js`)가 기대하는
 *   레지스터 형식(대문자 스네이크) 그대로 — 이 키들이 그대로 L_REGISTER.JSON_DATA 에
 *   저장되어 Register Address 화면(STREET01/STREET02 컬럼)에서 읽힘.
 */
export interface RegisterAddressRequest {
  entityCd: string;
  addressData: {
    CITY?: string;
    STATE?: string;
    COUNTRY?: string;
    POST_CODE?: string;
    STREET01?: string;
    STREET02?: string;
    STREET03?: string;
    SERVICE_CITY?: string;
    SERVICE_STATE?: string;
    SERVICE_COUNTRY?: string;
    SERVICE_POST_CODE?: string;
    SERVICE_STREET01?: string;
    SERVICE_STREET02?: string;
    SERVICE_STREET03?: string;
    [key: string]: string | undefined;
  };
  dateOfEffective?: string;
  note?: string;
  contactIdUen?: string;
  contactName?: string;
  /** PreSet(M-코드) 판별용 — backend 가 M-코드면 L_REGISTER 생략. */
  taskTypeCd?: string;
}

/**
 * Address 레지스터 등록 (MY 세션 전용)
 * POST /api/registerFromContact/my/address
 *   백엔드가 받은 addressData 의 STREET01/02/03/CITY/STATE/COUNTRY/POST_CODE 등을
 *   그대로 L_REGISTER.JSON_DATA 로 INSERT.
 */
export const registerAddress = async (
  data: RegisterAddressRequest,
): Promise<ApiResponse<RegisterFromContactResponse>> => {
  const response = await apiClient.post<RegisterFromContactResponse>('/registerFromContact/my/address', data);
  return response;
};

/**
 * SG Change of Registered Office Address (B32105) 레지스터 등록
 * POST /api/startTask/change-address/register
 *   updateEntityAddress=true 이면 L_ENTITY 의 등기 주소 컬럼도 함께 갱신.
 */
export interface RegisterSgChangeAddressRequest {
  entityCd: string;
  addressData: {
    postalCode?: string;
    blockNo?: string;
    streetName?: string;
    buildingName?: string;
    addrLevel?: string;
    addrUnit?: string;
    POSTAL_CODE?: string;
    BLOCK_NO?: string;
    STREET_NAME?: string;
    BUILDING_NAME?: string;
    ADDR_LEVEL?: string;
    ADDR_UNIT?: string;
  };
  dateOfEffective?: string;
  note?: string;
  contactIdUen?: string;
  contactName?: string;
  updateEntityAddress?: boolean;
}

export interface RegisterSgChangeAddressResponse {
  rgstCd?: string;
  entityCd?: string;
  success?: boolean;
  entityAddressUpdated?: boolean;
  message?: string;
}

export const registerSgChangeAddress = async (
  data: RegisterSgChangeAddressRequest,
): Promise<ApiResponse<RegisterSgChangeAddressResponse>> => {
  const response = await apiClient.post<RegisterSgChangeAddressResponse>(
    '/startTask/change-address/register',
    data,
  );
  return response;
};

/**
 * B32105 — L_ENTITY 등기 주소만 갱신 (레지스터 등록은 이미 완료된 경우)
 * POST /api/startTask/change-address/update-entity-address
 */
export const updateSgChangeAddressEntity = async (data: {
  entityCd: string;
  addressData: RegisterSgChangeAddressRequest['addressData'];
}): Promise<ApiResponse<{ success?: boolean; entityAddressUpdated?: boolean; message?: string }>> => {
  const response = await apiClient.post<{ success?: boolean; entityAddressUpdated?: boolean; message?: string }>(
    '/startTask/change-address/update-entity-address',
    data,
  );
  return response;
};

/**
 * Address 등기부 이력 조회 (REGISTER_WORK='A50008').
 * GET /api/registerFromContact/my/address/history/:entityCd?limit=N
 *   최신순. 기본 limit=2 (신/구 비교용).
 */
export interface AddressRegisterEntry {
  rgstCd: string;
  registerType: string;
  registerWork: string;
  json: Record<string, any> | null;
  registeredAt: string;
  registeredBy?: string;
  note?: string | null;
}

export const getAddressRegisterHistory = async (
  entityCd: string,
  limit: number = 2,
): Promise<ApiResponse<{ entityCd: string; count: number; entries: AddressRegisterEntry[] }>> => {
  const response = await apiClient.get<{ entityCd: string; count: number; entries: AddressRegisterEntry[] }>(
    `/registerFromContact/my/address/history/${entityCd}?limit=${limit}`,
  );
  return response;
};
